/**
 * Vid2Frames - Home View Controller Module (TypeScript)
 */

import { ExtractedFrame, ExtractionMode, OutputFormat } from '../types';
import { ExtractorEngine } from '../extractor-engine';

export class HomeView {

  // State Variables
  private static videoFile: File | null = null;
  private static videoUrl: string | null = null;
  private static videoDuration = 0;
  private static isExtracting = false;
  private static extractedFrames: ExtractedFrame[] = [];

  private static extractionMode: ExtractionMode = 'fps';
  private static fpsValue = 1;
  private static countValue = 10;
  private static outputFormat: OutputFormat = 'JPG';
  private static jpgQuality = 80;
  private static useTimeRange = false;
  private static startTime = 0;
  private static endTime = 0;

  // Photo Post Carousel State
  private static isPhotoPost = false;
  private static photoUrls: string[] = [];
  private static currentCarouselIndex = 0;

  // DOM Elements
  private static tabUpload: HTMLButtonElement;
  private static tabUrl: HTMLButtonElement;
  private static dropzoneBox: HTMLElement;
  private static urlBox: HTMLElement;
  private static videoUrlInput: HTMLInputElement;
  private static loadUrlBtn: HTMLButtonElement;
  private static videoFileInput: HTMLInputElement;
  private static playerContainer: HTMLElement;
  private static videoPlayer: HTMLVideoElement;
  private static changeVideoBtn: HTMLButtonElement;

  private static rowExtractionMode: HTMLElement;
  private static rowSamplingRate: HTMLElement;
  private static rowTimeRange: HTMLElement;
  private static carouselPreviewContainer: HTMLElement;
  private static carouselPreviewImg: HTMLImageElement;
  private static carouselBadge: HTMLElement;
  private static carouselPrevBtn: HTMLButtonElement;
  private static carouselNextBtn: HTMLButtonElement;
  private static carouselDots: HTMLElement;

  private static modeFpsBtn: HTMLButtonElement;
  private static modeCountBtn: HTMLButtonElement;
  private static stepperLabel: HTMLElement;
  private static stepperMinus: HTMLButtonElement;
  private static stepperPlus: HTMLButtonElement;
  private static stepperVal: HTMLElement;

  private static timeRangeToggle: HTMLInputElement;
  private static rangeSliderGroup: HTMLElement;
  private static rangeValText: HTMLElement;
  private static startTimeSlider: HTMLInputElement;
  private static endTimeSlider: HTMLInputElement;

  private static formatJpgBtn: HTMLButtonElement;
  private static formatPngBtn: HTMLButtonElement;
  private static jpgQualityGroup: HTMLElement;
  private static jpgQualitySlider: HTMLInputElement;
  private static qualityValText: HTMLElement;

  private static extractBtn: HTMLButtonElement;
  private static extractBtnText: HTMLElement;

  private static outputTitle: HTMLElement;
  private static emptyState: HTMLElement;
  private static resultsContent: HTMLElement;
  private static framesGrid: HTMLElement;
  private static downloadZipBtn: HTMLButtonElement;
  private static saveGalleryBtn: HTMLButtonElement;
  private static clearFramesBtn: HTMLButtonElement;

  private static progressBarFill: HTMLElement;
  private static progressText: HTMLElement;

  private static lightboxModal: HTMLElement;
  private static lightboxImg: HTMLImageElement;
  private static lightboxCloseBtn: HTMLButtonElement;
  private static lightboxDownloadBtn: HTMLAnchorElement;

  private static shareBtn: HTMLButtonElement;
  private static openTabBtn: HTMLButtonElement;
  private static shareModal: HTMLElement;
  private static closeShareModalBtn: HTMLButtonElement;
  private static shareOptionChatGpt: HTMLElement;
  private static shareOptionChatGptApi: HTMLElement;
  private static shareOptionGenerateComments: HTMLElement;
  private static shareOptionCopyImage: HTMLElement;
  private static shareOptionCopyPrompt: HTMLElement;
  private static shareOptionCopyLink: HTMLElement;
  private static imageCopyStatus: HTMLElement;
  private static promptCopyStatus: HTMLElement;
  private static linkCopyStatus: HTMLElement;
  private static toastNotification: HTMLElement;
  private static toastMessage: HTMLElement;

  public static init(): void {
    this.cacheDom();
    this.bindEvents();
    this.bindFaqAccordion();
    this.checkUrlParameters();
  }

  private static cacheDom(): void {
    this.tabUpload = document.getElementById('tab-upload') as HTMLButtonElement;
    this.tabUrl = document.getElementById('tab-url') as HTMLButtonElement;
    this.dropzoneBox = document.getElementById('dropzone-box') as HTMLElement;
    this.urlBox = document.getElementById('url-box') as HTMLElement;
    this.videoUrlInput = document.getElementById('video-url-input') as HTMLInputElement;
    this.loadUrlBtn = document.getElementById('load-url-btn') as HTMLButtonElement;
    this.videoFileInput = document.getElementById('video-file-input') as HTMLInputElement;
    this.playerContainer = document.getElementById('player-container') as HTMLElement;
    this.videoPlayer = document.getElementById('video-player') as HTMLVideoElement;
    this.changeVideoBtn = document.getElementById('change-video-btn') as HTMLButtonElement;

    this.modeFpsBtn = document.getElementById('mode-fps-btn') as HTMLButtonElement;
    this.modeCountBtn = document.getElementById('mode-count-btn') as HTMLButtonElement;
    this.stepperLabel = document.getElementById('stepper-label') as HTMLElement;
    this.stepperMinus = document.getElementById('step-minus') as HTMLButtonElement;
    this.stepperPlus = document.getElementById('step-plus') as HTMLButtonElement;
    this.stepperVal = document.getElementById('stepper-val') as HTMLElement;

    this.timeRangeToggle = document.getElementById('time-range-toggle') as HTMLInputElement;
    this.rangeSliderGroup = document.getElementById('range-slider-group') as HTMLElement;
    this.rangeValText = document.getElementById('range-val-text') as HTMLElement;
    this.startTimeSlider = document.getElementById('start-time-slider') as HTMLInputElement;
    this.endTimeSlider = document.getElementById('end-time-slider') as HTMLInputElement;

    this.formatJpgBtn = document.getElementById('format-jpg-btn') as HTMLButtonElement;
    this.formatPngBtn = document.getElementById('format-png-btn') as HTMLButtonElement;
    this.jpgQualityGroup = document.getElementById('jpg-quality-group') as HTMLElement;
    this.jpgQualitySlider = document.getElementById('jpg-quality-slider') as HTMLInputElement;
    this.qualityValText = document.getElementById('quality-val-text') as HTMLElement;

    this.extractBtn = document.getElementById('extract-btn') as HTMLButtonElement;
    this.extractBtnText = document.getElementById('extract-btn-text') as HTMLElement;

    this.outputTitle = document.getElementById('output-title') as HTMLElement;
    this.emptyState = document.getElementById('empty-state') as HTMLElement;
    this.resultsContent = document.getElementById('results-content') as HTMLElement;
    this.framesGrid = document.getElementById('frames-grid') as HTMLElement;
    this.downloadZipBtn = document.getElementById('download-zip-btn') as HTMLButtonElement;
    this.saveGalleryBtn = document.getElementById('save-gallery-btn') as HTMLButtonElement;
    this.clearFramesBtn = document.getElementById('clear-frames-btn') as HTMLButtonElement;

    this.progressBarFill = document.getElementById('progress-bar-fill') as HTMLElement;
    this.progressText = document.getElementById('progress-text') as HTMLElement;

    this.lightboxModal = document.getElementById('lightbox-modal') as HTMLElement;
    this.lightboxImg = document.getElementById('lightbox-img') as HTMLImageElement;
    this.lightboxCloseBtn = document.getElementById('lightbox-close-btn') as HTMLButtonElement;
    this.lightboxDownloadBtn = document.getElementById('lightbox-download-btn') as HTMLAnchorElement;

    this.shareBtn = document.getElementById('share-btn') as HTMLButtonElement;
    this.openTabBtn = document.getElementById('open-tab-btn') as HTMLButtonElement;
    this.shareModal = document.getElementById('share-modal') as HTMLElement;
    this.closeShareModalBtn = document.getElementById('close-share-modal-btn') as HTMLButtonElement;
    this.shareOptionChatGpt = document.getElementById('share-option-chatgpt') as HTMLElement;
    this.shareOptionChatGptApi = document.getElementById('share-option-chatgpt-api') as HTMLElement;
    this.shareOptionGenerateComments = document.getElementById('share-option-generate-comments') as HTMLElement;
    this.shareOptionCopyImage = document.getElementById('share-option-copyimage') as HTMLElement;
    this.shareOptionCopyPrompt = document.getElementById('share-option-copyprompt') as HTMLElement;
    this.shareOptionCopyLink = document.getElementById('share-option-copylink') as HTMLElement;
    this.imageCopyStatus = document.getElementById('image-copy-status') as HTMLElement;
    this.promptCopyStatus = document.getElementById('prompt-copy-status') as HTMLElement;
    this.linkCopyStatus = document.getElementById('link-copy-status') as HTMLElement;
    this.rowExtractionMode = document.getElementById('row-extraction-mode') as HTMLElement;
    this.rowSamplingRate = document.getElementById('row-sampling-rate') as HTMLElement;
    this.rowTimeRange = document.getElementById('row-time-range') as HTMLElement;
    this.carouselPreviewContainer = document.getElementById('carousel-preview-container') as HTMLElement;
    this.carouselPreviewImg = document.getElementById('carousel-preview-img') as HTMLImageElement;
    this.carouselBadge = document.getElementById('carousel-badge') as HTMLElement;
    this.carouselPrevBtn = document.getElementById('carousel-prev-btn') as HTMLButtonElement;
    this.carouselNextBtn = document.getElementById('carousel-next-btn') as HTMLButtonElement;
    this.carouselDots = document.getElementById('carousel-dots') as HTMLElement;

    this.toastNotification = document.getElementById('toast-notification') as HTMLElement;
    this.toastMessage = document.getElementById('toast-message') as HTMLElement;
  }

  private static bindEvents(): void {
    // Carousel Preview Navigation Arrows
    if (this.carouselPrevBtn) {
      this.carouselPrevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigateCarousel(-1);
      });
    }
    if (this.carouselNextBtn) {
      this.carouselNextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigateCarousel(1);
      });
    }

    // Tab switching between Upload Video and Video URL
    this.tabUpload.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUpload.classList.add('active');
      this.tabUrl.classList.remove('active');
      this.dropzoneBox.style.display = 'block';
      this.urlBox.style.display = 'none';
      if (this.playerContainer) this.playerContainer.style.display = 'none';
    });

    this.tabUrl.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUrl.classList.add('active');
      this.tabUpload.classList.remove('active');
      this.urlBox.style.display = 'block';
      this.dropzoneBox.style.display = 'none';
      if (this.playerContainer) this.playerContainer.style.display = 'none';
    });

    // Dropzone & File Pickers
    this.dropzoneBox.addEventListener('click', () => this.videoFileInput.click());
    this.dropzoneBox.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.add('dragover');
    });
    this.dropzoneBox.addEventListener('dragleave', () => this.dropzoneBox.classList.remove('dragover'));
    this.dropzoneBox.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.remove('dragover');
      if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
        this.handleFileSelection(e.dataTransfer.files[0]);
      }
    });
    this.videoFileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        this.handleFileSelection(target.files[0]);
      }
    });
    
    // Load Video URL
    this.loadUrlBtn.addEventListener('click', () => {
      const url = this.videoUrlInput.value.trim();
      if (!url) {
        alert('Please enter a valid video URL.');
        return;
      }
      this.handleUrlLoad(url);
    });

    this.changeVideoBtn.addEventListener('click', () => {
      this.setPhotoPostMode(false);
      this.playerContainer.style.display = 'none';
      if (this.tabUrl.classList.contains('active')) {
        this.urlBox.style.display = 'block';
        this.dropzoneBox.style.display = 'none';
      } else {
        this.dropzoneBox.style.display = 'block';
        this.urlBox.style.display = 'none';
        this.videoFileInput.click();
      }
    });

    // Mode Switcher
    this.modeFpsBtn.addEventListener('click', () => {
      this.extractionMode = 'fps';
      this.modeFpsBtn.classList.add('active');
      this.modeCountBtn.classList.remove('active');
      this.stepperLabel.textContent = 'Sampling Rate';
      this.updateStepperDisplay();
    });

    this.modeCountBtn.addEventListener('click', () => {
      this.extractionMode = 'count';
      this.modeCountBtn.classList.add('active');
      this.modeFpsBtn.classList.remove('active');
      this.stepperLabel.textContent = 'Total Frame Count';
      this.updateStepperDisplay();
    });

    // Stepper Controls
    this.stepperMinus.addEventListener('click', () => {
      if (this.extractionMode === 'fps') {
        if (this.fpsValue > 1) this.fpsValue--;
      } else {
        if (this.countValue > 1) this.countValue--;
      }
      this.updateStepperDisplay();
    });

    this.stepperPlus.addEventListener('click', () => {
      if (this.extractionMode === 'fps') {
        if (this.fpsValue < 60) this.fpsValue++;
      } else {
        if (this.countValue < 500) this.countValue++;
      }
      this.updateStepperDisplay();
    });

    // Time Range Trimmer
    this.timeRangeToggle.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.useTimeRange = target.checked;
      this.rangeSliderGroup.style.display = this.useTimeRange ? 'block' : 'none';
    });

    this.startTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (sVal >= eVal) {
        sVal = Math.max(0, eVal - 0.1);
        this.startTimeSlider.value = sVal.toString();
      }
      this.startTime = sVal;
      this.updateRangeDisplay();
    });

    this.endTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (eVal <= sVal) {
        eVal = Math.min(this.videoDuration, sVal + 0.1);
        this.endTimeSlider.value = eVal.toString();
      }
      this.endTime = eVal;
      this.updateRangeDisplay();
    });

    // Output Format & Quality
    this.formatJpgBtn.addEventListener('click', () => {
      this.outputFormat = 'JPG';
      this.formatJpgBtn.classList.add('active');
      this.formatPngBtn.classList.remove('active');
      this.jpgQualityGroup.style.display = 'block';
    });

    this.formatPngBtn.addEventListener('click', () => {
      this.outputFormat = 'PNG';
      this.formatPngBtn.classList.add('active');
      this.formatJpgBtn.classList.remove('active');
      this.jpgQualityGroup.style.display = 'none';
    });

    this.jpgQualitySlider.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      this.jpgQuality = parseInt(target.value, 10);
      this.qualityValText.textContent = `${this.jpgQuality}%`;
    });

    // Extract Trigger Button
    this.extractBtn.addEventListener('click', () => this.startExtraction());

    // Frame Output Actions
    this.clearFramesBtn.addEventListener('click', () => this.clearOutputState());

    this.downloadZipBtn.addEventListener('click', () => {
      ExtractorEngine.downloadZip(this.extractedFrames);
    });

    this.saveGalleryBtn.addEventListener('click', () => {
      ExtractorEngine.downloadAllIndividual(this.extractedFrames);
    });

    if (this.openTabBtn) {
      this.openTabBtn.addEventListener('click', async () => {
        if (this.extractedFrames && this.extractedFrames.length > 0) {
          const collageBlob = await this.createCollageBlob(this.extractedFrames);
          if (collageBlob && collageBlob.size > 0) {
            const objUrl = URL.createObjectURL(collageBlob);
            window.open(objUrl, '_blank');
            return;
          }
        }

        const shareUrl = this.getShareUrl();
        if (shareUrl) {
          window.open(shareUrl, '_blank');
        } else {
          alert('No video URL or extracted frames available. Please load a video first.');
        }
      });
    }

    // Lightbox Modal
    this.lightboxCloseBtn.addEventListener('click', () => this.lightboxModal.classList.remove('active'));
    this.lightboxModal.addEventListener('click', (e) => {
      if (e.target === this.lightboxModal) this.lightboxModal.classList.remove('active');
    });

    // Share Modal Events
    if (this.shareBtn) {
      this.shareBtn.addEventListener('click', () => {
        if (this.shareModal) this.shareModal.style.display = 'flex';
      });
    }

    if (this.closeShareModalBtn) {
      this.closeShareModalBtn.addEventListener('click', () => {
        if (this.shareModal) this.shareModal.style.display = 'none';
      });
    }

    if (this.shareModal) {
      this.shareModal.addEventListener('click', (e) => {
        if (e.target === this.shareModal) this.shareModal.style.display = 'none';
      });
    }

    const syncJobToCloudflare = async (): Promise<{ jobId?: string; viewerUrl?: string }> => {
      if (!this.extractedFrames || this.extractedFrames.length === 0) return {};

      try {
        const inputUrl = this.videoUrlInput ? this.videoUrlInput.value.trim() : '';
        const targetVideo = inputUrl || (this.videoUrl && !this.videoUrl.startsWith('blob:') ? this.videoUrl : '');

        const framePayloads = await Promise.all(
          this.extractedFrames.map(async (frame, idx) => {
            let dataUrl = '';
            if (frame.url && frame.url.startsWith('data:image/')) {
              dataUrl = frame.url;
            } else if (frame.blob) {
              dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(frame.blob);
              });
            }
            return {
              frameNumber: idx + 1,
              label: frame.filename || `Keyframe #${idx + 1}`,
              timestamp: frame.filename ? frame.filename.replace(/[^0-9\.]/g, '') + 's' : `${(idx * 2).toFixed(1)}s`,
              dataUrl
            };
          })
        );

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);

        const res = await fetch('https://vid2frames-worker.gvtechhub.workers.dev/api/v1/jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            videoUrl: targetVideo,
            title: `Vid2Frames Extraction (${this.extractedFrames.length} Keyframes)`,
            frames: framePayloads
          }),
          signal: controller.signal
        });
        clearTimeout(timer);

        if (!res.ok) return {};
        const data: any = await res.json();
        return { jobId: data.jobId, viewerUrl: data.viewerUrl };
      } catch (err) {
        console.warn('Sync job to Cloudflare Worker failed:', err);
        return {};
      }
    };

    const getGenerateCommentsPrompt = (syncedViewerUrl?: string): string => {
      const frameCount = this.extractedFrames.length || 6;
      const shareUrl = this.getShareUrl();
      const inputUrl = this.videoUrlInput ? this.videoUrlInput.value.trim() : '';
      const targetVideo = inputUrl || (this.videoUrl && !this.videoUrl.startsWith('blob:') ? this.videoUrl : '');
      const ssrWorkerUrl = syncedViewerUrl || `https://vid2frames-worker.gvtechhub.workers.dev/r?url=${encodeURIComponent(targetVideo)}&count=${frameCount}`;

      return `Please inspect the video keyframe gallery for this post:\n` +
        `🎥 Source Video: ${targetVideo || 'Uploaded Video'}\n` +
        `🖼️ Extracted Keyframes (Static HTML & Images): ${ssrWorkerUrl}\n` +
        `🌐 Interactive Web Viewer: ${shareUrl}\n\n` +
        `First, perform a quick visual analysis of the keyframes at ${ssrWorkerUrl}. Then, execute the following Comment Batch Formatting Prompt:\n\n` +
        `Comment Batch Formatting Prompt\n\n` +
        `Write a batch of 40–50 comments pertaining specifically to the post/images I provide.\n\n` +
        `Use a mix of Urban Dictionary/internet slang, roasting, rage bait, reply bait, non-roasting reactions, compliments, jokes, and casual comments.\n\n` +
        `Mix emoji usage naturally:\n\n` +
        `- Some comments with an emoji at the beginning\n` +
        `- Some with an emoji at the end\n` +
        `- Some with double emojis\n` +
        `- Some with only one emoji\n` +
        `- Some with no emojis\n` +
        `- A few comments that are emojis only\n\n` +
        `STRICT FORMATTING RULES:\n` +
        `Every individual comment MUST be on its own separate line/row.\n` +
        `ONE comment = ONE line.\n` +
        `Do NOT put two comments on the same line.\n` +
        `Do NOT combine the comments into paragraphs.\n` +
        `Do NOT insert blank lines between comments.\n` +
        `Do NOT number the comments.\n` +
        `Do NOT use bullets.\n` +
        `Do NOT add an introduction or explanation.\n` +
        `Do NOT use a writing block that collapses the line breaks.\n` +
        `Return the entire batch inside a plain-text code block so the line breaks are preserved when displayed or copied.\n\n` +
        `The output must look EXACTLY like this structurally:\n\n` +
        `😭 first comment goes here\n` +
        `second comment goes here 🔥\n` +
        `third comment goes here\n` +
        `💀 fourth comment goes here\n` +
        `fifth comment goes here 😭😭\n` +
        `💔\n` +
        `seventh comment goes here\n\n` +
        `Continue this exact one-comment-per-line format for all 40–50 comments.`;
    };

    const getAIPrompt = (syncedViewerUrl?: string): string => {
      const frameCount = this.extractedFrames.length || 6;
      const shareUrl = this.getShareUrl();
      const inputUrl = this.videoUrlInput ? this.videoUrlInput.value.trim() : '';
      const targetVideo = inputUrl || (this.videoUrl && !this.videoUrl.startsWith('blob:') ? this.videoUrl : '');
      const ssrWorkerUrl = syncedViewerUrl || `https://vid2frames-worker.gvtechhub.workers.dev/r?url=${encodeURIComponent(targetVideo)}&count=${frameCount}`;

      return `Please analyze this video keyframe extraction job for me:\n\n` +
        `🎥 Source Video: ${targetVideo || 'Uploaded Video'}\n` +
        `🖼️ Extracted Keyframes Count: ${frameCount}\n` +
        `🖼️ Direct Server-Side Rendered Frame Gallery (Static HTML & Images): ${ssrWorkerUrl}\n` +
        `🌐 Interactive Client Web Viewer: ${shareUrl}\n\n` +
        `API Extraction Endpoint:\n` +
        `POST https://vid2frames-worker.gvtechhub.workers.dev/api/v1/extract\n` +
        `Headers: { "Content-Type": "application/json", "X-API-Key": "v2f_live_efa4c4dfa52e9ccfd828de6aedae8336" }\n` +
        `Payload: { "videoUrl": "${targetVideo}", "countValue": ${frameCount} }\n\n` +
        `Please inspect the server-side frame gallery at ${ssrWorkerUrl} and provide a full 6-point visual analysis breakdown (story, facial expressions, setting, on-screen text, retention hook, and keyframe evaluation).`;
    };

    if (this.shareOptionChatGpt) {
      this.shareOptionChatGpt.addEventListener('click', async () => {
        // Open tab synchronously so browser popup blocker permits it
        const newTab = window.open('about:blank', '_blank');
        if (this.shareModal) this.shareModal.style.display = 'none';
        this.showToast('⚡ Syncing keyframes to AI server...');

        let syncedViewerUrl: string | undefined;
        try {
          const synced = await syncJobToCloudflare();
          syncedViewerUrl = synced.viewerUrl;
        } catch (e) {
          console.warn('Sync failed:', e);
        }

        const promptText = getAIPrompt(syncedViewerUrl);
        const chatGPTUrl = `https://chatgpt.com/?q=${encodeURIComponent(promptText)}`;

        try {
          await HomeView.copyFramesToClipboard();
        } catch (e) {
          console.warn('Copy frames failed:', e);
        }

        if (newTab) {
          newTab.location.href = chatGPTUrl;
        } else {
          window.location.href = chatGPTUrl;
        }

        this.showToast('📋 Prompt generated & ChatGPT opened!');
      });
    }

    const getAPIPrompt = (syncedViewerUrl?: string): string => {
      const inputUrl = this.videoUrlInput ? this.videoUrlInput.value.trim() : '';
      const targetVideo = inputUrl || (this.videoUrl && !this.videoUrl.startsWith('blob:') ? this.videoUrl : '');
      const modeStr = this.extractionMode === 'fps' ? `${this.fpsValue} FPS` : `${this.countValue} Total Images`;
      const rangeStr = this.useTimeRange ? `${this.startTime.toFixed(1)}s - ${this.endTime.toFixed(1)}s` : 'Full Video';
      const formatStr = this.outputFormat === 'JPG' ? `JPG (${this.jpgQuality}% Quality)` : 'PNG';
      const frameCount = this.extractedFrames.length || this.countValue || 6;
      const ssrViewerUrl = syncedViewerUrl || `https://vid2frames-worker.gvtechhub.workers.dev/r?url=${encodeURIComponent(targetVideo)}&count=${frameCount}`;

      const payload = {
        videoUrl: targetVideo || 'https://www.tiktok.com/@user/video/123456789',
        extractionMode: this.extractionMode,
        fpsValue: this.extractionMode === 'fps' ? this.fpsValue : undefined,
        countValue: this.extractionMode === 'count' ? this.countValue : undefined,
        useTimeRange: this.useTimeRange,
        startTime: this.useTimeRange ? this.startTime : undefined,
        endTime: this.useTimeRange ? this.endTime : undefined,
        outputFormat: this.outputFormat,
        jpgQuality: this.outputFormat === 'JPG' ? this.jpgQuality : undefined
      };

      return `Please analyze this video by calling the Vid2Frames API endpoint with the exact user job parameters below:\n\n` +
        `🎥 Target Video: ${targetVideo || 'Uploaded Video'}\n` +
        `⚙️ Job Extraction Parameters:\n` +
        `  • Mode: ${modeStr}\n` +
        `  • Time Trimmer: ${rangeStr}\n` +
        `  • Output Format: ${formatStr}\n\n` +
        `🖼️ Server-Side Rendered Frame Gallery (inspect this first): ${ssrViewerUrl}\n\n` +
        `🌐 Vid2Frames API Endpoint:\n` +
        `POST https://vid2frames-worker.gvtechhub.workers.dev/api/v1/extract\n` +
        `Headers: { "Content-Type": "application/json", "X-API-Key": "v2f_live_efa4c4dfa52e9ccfd828de6aedae8336" }\n` +
        `Payload: ${JSON.stringify(payload, null, 2)}\n\n` +
        `Please inspect the server-side frame gallery at ${ssrViewerUrl} and provide a comprehensive visual analysis and timeline breakdown.`;
    };

    if (this.shareOptionChatGptApi) {
      this.shareOptionChatGptApi.addEventListener('click', async () => {
        const newTab = window.open('about:blank', '_blank');
        if (this.shareModal) this.shareModal.style.display = 'none';
        this.showToast('⚡ Syncing keyframes to AI server...');

        let syncedViewerUrl: string | undefined;
        try {
          const synced = await syncJobToCloudflare();
          syncedViewerUrl = synced.viewerUrl;
        } catch (e) {
          console.warn('Sync failed:', e);
        }

        const promptText = getAPIPrompt(syncedViewerUrl);
        const chatGPTUrl = `https://chatgpt.com/?q=${encodeURIComponent(promptText)}`;

        try {
          await HomeView.copyFramesToClipboard();
        } catch (e) {
          console.warn('Copy frames failed:', e);
        }

        if (newTab) {
          newTab.location.href = chatGPTUrl;
        } else {
          window.location.href = chatGPTUrl;
        }

        this.showToast('⚡ API analysis prompt generated & ChatGPT opened!');
      });
    }

    if (this.shareOptionGenerateComments) {
      this.shareOptionGenerateComments.addEventListener('click', async () => {
        const newTab = window.open('about:blank', '_blank');
        if (this.shareModal) this.shareModal.style.display = 'none';
        this.showToast('⚡ Syncing keyframes for AI comment generation...');

        let syncedViewerUrl: string | undefined;
        try {
          const synced = await syncJobToCloudflare();
          syncedViewerUrl = synced.viewerUrl;
        } catch (e) {
          console.warn('Sync failed:', e);
        }

        const promptText = getGenerateCommentsPrompt(syncedViewerUrl);
        const chatGPTUrl = `https://chatgpt.com/?q=${encodeURIComponent(promptText)}`;

        try {
          await HomeView.copyFramesToClipboard();
        } catch (e) {
          console.warn('Copy frames failed:', e);
        }

        if (newTab) {
          newTab.location.href = chatGPTUrl;
        } else {
          window.location.href = chatGPTUrl;
        }

        this.showToast('💬 Comment generation prompt sent to ChatGPT!');
      });
    }

    if (this.shareOptionCopyImage) {
      this.shareOptionCopyImage.addEventListener('click', async () => {
        const copied = await HomeView.copyFramesToClipboard();
        if (copied) {
          if (this.imageCopyStatus) {
            this.imageCopyStatus.innerText = 'Copied!';
            this.imageCopyStatus.style.background = '#10B981';
            this.imageCopyStatus.style.color = '#fff';
            setTimeout(() => {
              if (this.imageCopyStatus) {
                this.imageCopyStatus.innerText = 'Copy Image';
                this.imageCopyStatus.style.background = '#27272a';
                this.imageCopyStatus.style.color = '#e4e4e7';
              }
            }, 2000);
          }
          this.showToast('📋 Keyframe collage copied to clipboard!');
        } else {
          this.showToast('ℹ️ Extract frames first to copy image collage.');
        }
      });
    }

    if (this.shareOptionCopyPrompt) {
      this.shareOptionCopyPrompt.addEventListener('click', () => {
        const promptText = getAIPrompt();
        navigator.clipboard.writeText(promptText);
        if (this.promptCopyStatus) {
          this.promptCopyStatus.innerText = 'Copied!';
          this.promptCopyStatus.style.background = '#10B981';
          this.promptCopyStatus.style.color = '#fff';
          setTimeout(() => {
            if (this.promptCopyStatus) {
              this.promptCopyStatus.innerText = 'Copy';
              this.promptCopyStatus.style.background = '#27272a';
              this.promptCopyStatus.style.color = '#e4e4e7';
            }
          }, 2000);
        }
        this.showToast('📋 AI prompt copied to clipboard!');
      });
    }

    if (this.shareOptionCopyLink) {
      this.shareOptionCopyLink.addEventListener('click', () => {
        const shareUrl = this.getShareUrl();
        navigator.clipboard.writeText(shareUrl);
        if (this.linkCopyStatus) {
          this.linkCopyStatus.innerText = 'Copied!';
          this.linkCopyStatus.style.background = '#10B981';
          this.linkCopyStatus.style.color = '#fff';
          setTimeout(() => {
            if (this.linkCopyStatus) {
              this.linkCopyStatus.innerText = 'Copy Link';
              this.linkCopyStatus.style.background = '#27272a';
              this.linkCopyStatus.style.color = '#e4e4e7';
            }
          }, 2000);
        }
        this.showToast('🌐 Shareable link copied to clipboard!');
      });
    }
  }

  private static getShareUrl(): string {
    const inputUrl = this.videoUrlInput ? this.videoUrlInput.value.trim() : '';
    const targetVideo = inputUrl || (this.videoUrl && !this.videoUrl.startsWith('blob:') ? this.videoUrl : '');
    const baseUrl = window.location.origin + window.location.pathname;

    if (!targetVideo) return window.location.href;

    const params = new URLSearchParams();
    params.set('url', targetVideo);
    params.set('mode', this.extractionMode);

    if (this.extractionMode === 'fps') {
      params.set('fps', this.fpsValue.toString());
    } else {
      params.set('count', this.countValue.toString());
    }

    params.set('format', this.outputFormat);
    if (this.outputFormat === 'JPG') {
      params.set('quality', this.jpgQuality.toString());
    }

    if (this.useTimeRange) {
      params.set('range', 'true');
      params.set('start', this.startTime.toFixed(1));
      params.set('end', this.endTime.toFixed(1));
    }

    params.set('view', 'results');
    params.set('autoExtract', 'true');

    return `${baseUrl}?${params.toString()}`;
  }

  private static checkUrlParameters(): void {
    const params = new URLSearchParams(window.location.search);
    const targetUrl = params.get('url') || params.get('videoUrl') || params.get('share');
    const isResultsOnly = params.get('view') === 'results' || params.get('mode') === 'results' || params.get('results') === '1';

    // Hydrate job settings from URL parameters
    if (params.has('mode')) {
      const modeParam = params.get('mode');
      if (modeParam === 'fps' || modeParam === 'count') {
        this.extractionMode = modeParam;
        if (this.modeFpsBtn && this.modeCountBtn) {
          if (modeParam === 'fps') {
            this.modeFpsBtn.classList.add('active');
            this.modeCountBtn.classList.remove('active');
            if (this.stepperLabel) this.stepperLabel.textContent = 'Sampling Rate';
          } else {
            this.modeCountBtn.classList.add('active');
            this.modeFpsBtn.classList.remove('active');
            if (this.stepperLabel) this.stepperLabel.textContent = 'Frame Count';
          }
        }
      }
    }

    if (params.has('fps')) {
      const fpsVal = parseFloat(params.get('fps') || '1');
      if (!isNaN(fpsVal) && fpsVal > 0) {
        this.fpsValue = fpsVal;
      }
    }

    if (params.has('count')) {
      const countVal = parseInt(params.get('count') || '10', 10);
      if (!isNaN(countVal) && countVal > 0) {
        this.countValue = countVal;
      }
    }

    this.updateStepperDisplay();

    if (params.has('format')) {
      const formatParam = params.get('format')?.toUpperCase();
      if (formatParam === 'JPG' || formatParam === 'PNG') {
        this.outputFormat = formatParam as OutputFormat;
        if (this.formatJpgBtn && this.formatPngBtn) {
          if (formatParam === 'JPG') {
            this.formatJpgBtn.classList.add('active');
            this.formatPngBtn.classList.remove('active');
            if (this.jpgQualityGroup) this.jpgQualityGroup.style.display = 'block';
          } else {
            this.formatPngBtn.classList.add('active');
            this.formatJpgBtn.classList.remove('active');
            if (this.jpgQualityGroup) this.jpgQualityGroup.style.display = 'none';
          }
        }
      }
    }

    if (params.has('quality')) {
      const qVal = parseInt(params.get('quality') || '80', 10);
      if (!isNaN(qVal)) {
        this.jpgQuality = qVal;
        if (this.jpgQualitySlider) this.jpgQualitySlider.value = qVal.toString();
        if (this.qualityValText) this.qualityValText.textContent = `${qVal}%`;
      }
    }

    if (params.has('range') && params.get('range') === 'true') {
      this.useTimeRange = true;
      if (this.timeRangeToggle) this.timeRangeToggle.checked = true;
      if (this.rangeSliderGroup) this.rangeSliderGroup.style.display = 'block';

      if (params.has('start')) {
        const s = parseFloat(params.get('start') || '0');
        if (!isNaN(s)) this.startTime = s;
      }
      if (params.has('end')) {
        const e = parseFloat(params.get('end') || '0');
        if (!isNaN(e)) this.endTime = e;
      }
      this.updateRangeDisplay();
    }

    if (isResultsOnly) {
      document.body.classList.add('results-only-view');
      if (this.emptyState) this.emptyState.style.display = 'none';
      if (this.resultsContent) this.resultsContent.style.display = 'flex';

      const outputHeader = document.querySelector('#output-card .output-header');
      if (outputHeader && !document.getElementById('back-to-app-link')) {
        const navBanner = document.createElement('div');
        navBanner.id = 'back-to-app-link';
        navBanner.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid rgba(255,255,255,0.08);';
        const originUrl = window.location.origin + window.location.pathname;
        navBanner.innerHTML = `
          <a href="${originUrl}" style="color: #38bdf8; text-decoration: none; font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; gap: 6px;">
            ← Open Full Vid2Frames Workspace
          </a>
          <span style="font-size: 11px; color: #a1a1aa; background: rgba(255,255,255,0.05); padding: 4px 10px; border-radius: 20px; border: 1px solid rgba(255,255,255,0.08);">
            Dedicated Frame Results Screen
          </span>
        `;
        outputHeader.parentNode?.insertBefore(navBanner, outputHeader);
      }
    }

    if (targetUrl) {
      if (this.tabUrl) this.tabUrl.click();
      if (this.videoUrlInput) this.videoUrlInput.value = targetUrl;
      const autoExtract = params.get('autoExtract') === 'true' || params.get('autoExtract') === '1' || params.has('autoExtract');
      this.handleUrlLoad(targetUrl, autoExtract);
    }
  }

  private static showToast(message: string): void {
    if (!this.toastNotification || !this.toastMessage) return;
    this.toastMessage.innerText = message;
    this.toastNotification.style.display = 'flex';
    setTimeout(() => {
      if (this.toastNotification) this.toastNotification.style.display = 'none';
    }, 5000);
  }

  private static async ensurePngBlob(blob: Blob): Promise<Blob> {
    if (blob.type === 'image/png') return blob;
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          return resolve(blob);
        }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((pngBlob) => {
          resolve(pngBlob || blob);
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };
      img.src = url;
    });
  }

  private static async copyFramesToClipboard(): Promise<boolean> {
    if (!this.extractedFrames || this.extractedFrames.length === 0) return false;
    try {
      let pngBlob: Blob;
      if (this.extractedFrames.length === 1) {
        pngBlob = await this.ensurePngBlob(this.extractedFrames[0].blob);
      } else {
        pngBlob = await this.createCollageBlob(this.extractedFrames);
      }
      if (pngBlob && navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': pngBlob })
        ]);
        return true;
      }
    } catch (err) {
      console.warn('Clipboard image write failed:', err);
    }
    return false;
  }

  private static async createCollageBlob(frames: ExtractedFrame[]): Promise<Blob> {
    if (!frames || frames.length === 0) return new Blob();

    const maxFrames = frames.length;
    const imageElements: HTMLImageElement[] = await Promise.all(
      frames.slice(0, maxFrames).map((frame) => {
        return new Promise<HTMLImageElement>((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(img);
          img.src = frame.url;
        });
      })
    );

    const cols = Math.ceil(Math.sqrt(imageElements.length));
    const rows = Math.ceil(imageElements.length / cols);

    const cellWidth = imageElements[0].naturalWidth || 640;
    const cellHeight = imageElements[0].naturalHeight || 360;

    const canvas = document.createElement('canvas');
    canvas.width = cols * cellWidth;
    canvas.height = rows * cellHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return this.ensurePngBlob(frames[0].blob);
    }

    ctx.fillStyle = '#090a0f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    imageElements.forEach((img, i) => {
      const r = Math.floor(i / cols);
      const c = i % cols;
      ctx.drawImage(img, c * cellWidth, r * cellHeight, cellWidth, cellHeight);
    });

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else resolve(this.ensurePngBlob(frames[0].blob));
      }, 'image/png');
    });
  }

  private static handleFileSelection(file: File): void {
    if (!file.type.startsWith('video/') && !/\.(mp4|mov|avi|webm|mkv)$/i.test(file.name)) {
      alert('Please select a valid video file.');
      return;
    }

    this.setPhotoPostMode(false);
    this.videoFile = file;
    if (this.videoUrl && this.videoUrl.startsWith('blob:')) URL.revokeObjectURL(this.videoUrl);
    this.videoUrl = URL.createObjectURL(file);

    this.videoPlayer.src = this.videoUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      this.dropzoneBox.style.display = 'none';
      this.urlBox.style.display = 'none';
      this.playerContainer.style.display = 'flex';
    };
  }

  private static extractYouTubeId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  private static async safeFetchJson(url: string, options?: RequestInit): Promise<any | null> {
    try {
      const res = await fetch(url, options);
      if (!res.ok) return null;
      const text = await res.text();
      if (!text || !text.trim()) return null;
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  private static async handleUrlLoad(inputUrl: string, autoExtract: boolean = false): Promise<void> {
    let targetUrl = inputUrl.trim().replace(/^http:\/\//i, 'https://');
    if (!targetUrl.startsWith('https://')) {
      targetUrl = 'https://' + targetUrl;
    }

    this.loadUrlBtn.disabled = true;
    this.loadUrlBtn.textContent = 'Resolving...';

    try {
      // 1. TikTok URL resolution via Multi-Tier Fallback Strategy
      if (/tiktok\.com/i.test(targetUrl)) {
        let isPhoto = false;
        let photoImages: string[] = [];
        let videoStreamUrl = '';
        let resolved = false;

        // Tier A: Cloudflare Worker /api/v1/resolve
        const resolveData = await this.safeFetchJson('https://vid2frames-worker.gvtechhub.workers.dev/api/v1/resolve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: targetUrl })
        });
        if (resolveData && resolveData.success) {
          if (resolveData.isPhoto && Array.isArray(resolveData.images) && resolveData.images.length > 0) {
            isPhoto = true;
            photoImages = resolveData.images;
            resolved = true;
          } else if (resolveData.videoUrl) {
            videoStreamUrl = resolveData.videoUrl;
            resolved = true;
          }
        }

        // Tier B: Cloudflare Worker /api/v1/extract (live edge fallback)
        if (!resolved) {
          const extractData = await this.safeFetchJson('https://vid2frames-worker.gvtechhub.workers.dev/api/v1/extract', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ videoUrl: targetUrl, count: 20 })
          });
          if (extractData && extractData.success) {
            if (Array.isArray(extractData.rawImages) && extractData.rawImages.length > 0) {
              isPhoto = true;
              photoImages = extractData.rawImages;
              resolved = true;
            } else if (Array.isArray(extractData.frames) && extractData.frames.length > 0) {
              const slideFrames = extractData.frames.filter((f: any) => f.label && f.label.startsWith('Slideshow Image'));
              if (slideFrames.length > 0) {
                isPhoto = true;
                photoImages = slideFrames.map((f: any) => f.url);
                resolved = true;
              }
            }
          }
        }

        // Tier C: Direct TikWM API endpoints + CORS Proxy mirrors
        if (!resolved) {
          const tikwmEndpoints = [
            `https://www.tikwm.com/api/?url=${encodeURIComponent(targetUrl)}&hd=1`,
            `https://api.tikwm.com/api/?url=${encodeURIComponent(targetUrl)}&hd=1`,
            `https://tikwm.com/api/?url=${encodeURIComponent(targetUrl)}&hd=1`,
            `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://www.tikwm.com/api/?url=${encodeURIComponent(targetUrl)}&hd=1`)}`
          ];

          for (const ep of tikwmEndpoints) {
            const data = await this.safeFetchJson(ep);
            if (data && data.code === 0 && data.data) {
              const d = data.data;
              if (Array.isArray(d.images) && d.images.length > 0) {
                isPhoto = true;
                photoImages = d.images.map((img: string) =>
                  img.startsWith('http') ? img : `https://www.tikwm.com${img}`
                );
                resolved = true;
                break;
              } else if (d.play) {
                videoStreamUrl = d.play.startsWith('http') ? d.play : `https://www.tikwm.com${d.play}`;
                resolved = true;
                break;
              }
            }
          }
        }

        if (resolved) {
          if (isPhoto && photoImages.length > 0) {
            this.videoFile = null;
            this.videoUrl = inputUrl;
            this.setPhotoPostMode(true, photoImages);

            this.loadUrlBtn.disabled = false;
            this.loadUrlBtn.textContent = 'Load';
            this.dropzoneBox.style.display = 'none';
            this.urlBox.style.display = 'none';
            this.playerContainer.style.display = 'flex';

            if (autoExtract) {
              setTimeout(() => {
                this.startExtraction();
              }, 300);
            }
            return;
          } else if (videoStreamUrl) {
            this.setPhotoPostMode(false);
            targetUrl = videoStreamUrl;
          }
        } else {
          throw new Error('Unable to parse TikTok link. Please ensure the post is public and accessible.');
        }
      }
      // 2. YouTube URL resolution via Invidious instances & Cobalt API
      else if (/youtube\.com|youtu\.be/i.test(targetUrl)) {
        this.setPhotoPostMode(false);
        const ytId = this.extractYouTubeId(targetUrl);
        let resolved = false;

        // Method A: Invidious Instances
        if (ytId) {
          const invidiousInstances = [
            'https://inv.tux.pizza',
            'https://invidious.nerdvpn.de',
            'https://invidious.flokinet.to',
            'https://invidious.privacydev.net'
          ];
          for (const instance of invidiousInstances) {
            const data = await this.safeFetchJson(`${instance}/api/v1/videos/${ytId}`);
            if (data) {
              const mp4Format = data.formatStreams?.find((f: any) => f.container === 'mp4' || f.encoding === 'h264');
              if (mp4Format && mp4Format.url) {
                targetUrl = mp4Format.url;
                resolved = true;
                break;
              }
            }
          }
        }

        // Method B: Cobalt API
        if (!resolved) {
          const data = await this.safeFetchJson('https://api.cobalt.tools/', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: targetUrl })
          });
          if (data && data.url) {
            targetUrl = data.url;
            resolved = true;
          }
        }

        if (!resolved) {
          throw new Error('Unable to extract stream from YouTube link. Please ensure link is public.');
        }
      }
    } catch (err: any) {
      alert(`Error loading video URL: ${err.message || 'Invalid or unsupported link'}`);
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      return;
    }

    this.videoFile = null;
    this.videoUrl = targetUrl;

    this.videoPlayer.crossOrigin = 'anonymous';
    this.videoPlayer.src = targetUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      this.dropzoneBox.style.display = 'none';
      this.urlBox.style.display = 'none';
      this.playerContainer.style.display = 'flex';

      if (autoExtract) {
        setTimeout(() => {
          this.startExtraction();
        }, 300);
      }
    };

    this.videoPlayer.onerror = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      alert('Failed to load video stream from URL. Please ensure link is accessible.');
    };
  }

  private static updateStepperDisplay(): void {
    if (this.extractionMode === 'fps') {
      this.stepperVal.textContent = `${this.fpsValue} FPS`;
    } else {
      this.stepperVal.textContent = `${this.countValue} IMAGES`;
    }
  }

  private static updateRangeDisplay(): void {
    this.rangeValText.textContent = `${this.startTime.toFixed(1)}s - ${this.endTime.toFixed(1)}s`;
  }

  private static async startExtraction(): Promise<void> {
    if (this.isPhotoPost) {
      if (!this.photoUrls || this.photoUrls.length === 0) return;
      if (this.isExtracting) return;

      this.isExtracting = true;
      this.extractBtn.disabled = true;
      this.extractBtnText.textContent = 'Extracting Photos...';

      this.clearOutputState();
      this.emptyState.style.display = 'none';
      this.resultsContent.style.display = 'flex';
      this.progressBarFill.style.width = '0%';
      this.progressText.textContent = '0%';

      if (this.resultsContent) {
        this.resultsContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      this.extractedFrames = await ExtractorEngine.extractPhotos({
        photoUrls: this.photoUrls,
        outputFormat: this.outputFormat,
        jpgQuality: this.jpgQuality,
        onProgress: (percent) => {
          this.progressBarFill.style.width = `${percent}%`;
          this.progressText.textContent = `${percent}%`;
        },
        onFrameExtracted: (frameItem, currentCount, totalCount) => {
          this.renderFrameCard(frameItem, currentCount - 1);
          this.outputTitle.textContent = `Extracted Photos (${currentCount}/${totalCount})`;
        }
      });

      this.isExtracting = false;
      this.extractBtn.disabled = false;
      this.extractBtnText.textContent = `Extract Photos (${this.photoUrls.length})`;

      if (this.resultsContent) {
        this.resultsContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (!this.videoFile && !this.videoUrl) {
      if (this.tabUrl.classList.contains('active')) {
        const url = this.videoUrlInput.value.trim();
        if (url) {
          this.handleUrlLoad(url);
          return;
        } else {
          this.videoUrlInput.focus();
          alert('Please enter a video URL first.');
          return;
        }
      } else {
        this.videoFileInput.click();
        return;
      }
    }

    if (this.isExtracting) return;

    this.isExtracting = true;
    this.extractBtn.disabled = true;
    this.extractBtnText.textContent = 'Extracting...';

    this.clearOutputState();
    this.emptyState.style.display = 'none';
    this.resultsContent.style.display = 'flex';
    this.progressBarFill.style.width = '0%';
    this.progressText.textContent = '0%';

    if (this.resultsContent) {
      this.resultsContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    this.extractedFrames = await ExtractorEngine.extractFrames({
      videoElement: this.videoPlayer,
      videoDuration: this.videoDuration,
      extractionMode: this.extractionMode,
      fpsValue: this.fpsValue,
      countValue: this.countValue,
      useTimeRange: this.useTimeRange,
      startTime: this.startTime,
      endTime: this.endTime,
      outputFormat: this.outputFormat,
      jpgQuality: this.jpgQuality,
      onProgress: (percent) => {
        this.progressBarFill.style.width = `${percent}%`;
        this.progressText.textContent = `${percent}%`;
      },
      onFrameExtracted: (frameItem, currentCount) => {
        this.renderFrameCard(frameItem, currentCount - 1);
        this.outputTitle.textContent = `Extracted Frames (${currentCount})`;
      }
    });

    this.isExtracting = false;
    this.extractBtn.disabled = false;
    this.extractBtnText.textContent = 'Extract Frames';

    if (this.resultsContent) {
      this.resultsContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private static setPhotoPostMode(enabled: boolean, photoList: string[] = []): void {
    this.isPhotoPost = enabled;
    this.photoUrls = photoList;
    this.currentCarouselIndex = 0;

    const noticeId = 'photo-post-notice-msg';
    let noticeEl = document.getElementById(noticeId);

    if (enabled) {
      if (this.rowExtractionMode) this.rowExtractionMode.classList.add('settings-disabled');
      if (this.rowSamplingRate) this.rowSamplingRate.classList.add('settings-disabled');
      if (this.rowTimeRange) this.rowTimeRange.classList.add('settings-disabled');
      if (this.rangeSliderGroup) this.rangeSliderGroup.classList.add('settings-disabled');

      if (this.modeFpsBtn) this.modeFpsBtn.disabled = true;
      if (this.modeCountBtn) this.modeCountBtn.disabled = true;
      if (this.stepperMinus) this.stepperMinus.disabled = true;
      if (this.stepperPlus) this.stepperPlus.disabled = true;
      if (this.timeRangeToggle) this.timeRangeToggle.disabled = true;

      if (!noticeEl && this.rowExtractionMode && this.rowExtractionMode.parentNode) {
        noticeEl = document.createElement('div');
        noticeEl.id = noticeId;
        noticeEl.className = 'photo-post-notice';
        this.rowExtractionMode.parentNode.insertBefore(noticeEl, this.rowExtractionMode);
      }
      if (noticeEl) {
        noticeEl.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <span><strong>TikTok Photo Post Detected:</strong> Frame extraction options are disabled for photo posts. All ${this.photoUrls.length} carousel photo(s) will be exported in your chosen format.</span>
        `;
        noticeEl.style.display = 'flex';
      }

      if (this.videoPlayer) this.videoPlayer.style.display = 'none';
      if (this.carouselPreviewContainer) this.carouselPreviewContainer.style.display = 'flex';

      if (this.extractBtnText) {
        this.extractBtnText.textContent = `Extract Photos (${this.photoUrls.length})`;
      }

      this.updateCarouselDisplay();
    } else {
      if (this.rowExtractionMode) this.rowExtractionMode.classList.remove('settings-disabled');
      if (this.rowSamplingRate) this.rowSamplingRate.classList.remove('settings-disabled');
      if (this.rowTimeRange) this.rowTimeRange.classList.remove('settings-disabled');
      if (this.rangeSliderGroup) this.rangeSliderGroup.classList.remove('settings-disabled');

      if (this.modeFpsBtn) this.modeFpsBtn.disabled = false;
      if (this.modeCountBtn) this.modeCountBtn.disabled = false;
      if (this.stepperMinus) this.stepperMinus.disabled = false;
      if (this.stepperPlus) this.stepperPlus.disabled = false;
      if (this.timeRangeToggle) this.timeRangeToggle.disabled = false;

      if (noticeEl) noticeEl.style.display = 'none';

      if (this.videoPlayer) this.videoPlayer.style.display = 'block';
      if (this.carouselPreviewContainer) this.carouselPreviewContainer.style.display = 'none';

      if (this.extractBtnText) {
        this.extractBtnText.textContent = 'Extract Frames';
      }
    }
  }

  private static navigateCarousel(delta: number): void {
    if (!this.photoUrls || this.photoUrls.length === 0) return;
    this.currentCarouselIndex = (this.currentCarouselIndex + delta + this.photoUrls.length) % this.photoUrls.length;
    this.updateCarouselDisplay();
  }

  private static updateCarouselDisplay(): void {
    if (!this.photoUrls || this.photoUrls.length === 0) return;

    const currentUrl = this.photoUrls[this.currentCarouselIndex];
    if (this.carouselPreviewImg) {
      this.carouselPreviewImg.src = currentUrl.includes('wsrv.nl') ? currentUrl : `https://wsrv.nl/?url=${encodeURIComponent(currentUrl)}`;
    }

    if (this.carouselBadge) {
      this.carouselBadge.textContent = `Photo ${this.currentCarouselIndex + 1} of ${this.photoUrls.length}`;
    }

    if (this.carouselPrevBtn && this.carouselNextBtn) {
      this.carouselPrevBtn.style.display = this.photoUrls.length > 1 ? 'flex' : 'none';
      this.carouselNextBtn.style.display = this.photoUrls.length > 1 ? 'flex' : 'none';
    }

    if (this.carouselDots) {
      this.carouselDots.innerHTML = '';
      if (this.photoUrls.length > 1) {
        this.photoUrls.forEach((_, idx) => {
          const dot = document.createElement('div');
          dot.className = `carousel-dot ${idx === this.currentCarouselIndex ? 'active' : ''}`;
          dot.addEventListener('click', () => {
            this.currentCarouselIndex = idx;
            this.updateCarouselDisplay();
          });
          this.carouselDots.appendChild(dot);
        });
      }
    }
  }

  private static clearOutputState(): void {
    this.extractedFrames.forEach(f => URL.revokeObjectURL(f.url));
    this.extractedFrames = [];
    this.framesGrid.innerHTML = '';
    this.outputTitle.textContent = 'Extracted Frames (0)';
    this.resultsContent.style.display = 'none';
    this.emptyState.style.display = 'flex';
  }

  private static renderFrameCard(frameItem: ExtractedFrame, index: number): void {
    const card = document.createElement('div');
    card.className = 'frame-thumb';
    card.innerHTML = `
      <img src="${frameItem.url}" alt="${frameItem.filename}">
      <div class="frame-thumb-label">${frameItem.filename}</div>
    `;
    card.addEventListener('click', () => this.openLightbox(index));
    this.framesGrid.appendChild(card);
  }

  private static openLightbox(index: number): void {
    const frame = this.extractedFrames[index];
    if (!frame) return;
    this.lightboxImg.src = frame.url;
    this.lightboxDownloadBtn.href = frame.url;
    this.lightboxDownloadBtn.download = frame.filename;
    this.lightboxModal.classList.add('active');
  }

  private static bindFaqAccordion(): void {
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const q = item.querySelector('.faq-question');
      if (q) {
        q.addEventListener('click', () => item.classList.toggle('active'));
      }
    });
  }
}
